/**
 * A fila de etiquetas a imprimir — F124, fatia 3; guardada no computador na F136.
 *
 * ## Por que no computador e não numa tabela
 *
 * Uma tabela seria uma tabela de linhas órfãs que ninguém apaga, e subiria com
 * migração e rota nova. Mas a fila que morria com a aba (`sessionStorage`) não
 * serve à loja: o trocador junta ao longo do dia "os que vou tirar o selo",
 * fecha o navegador, e a lista some. Por isso `localStorage` — sobrevive a
 * fechar o navegador e o app, naquele computador — e o evento `storage` mantém
 * as abas e janelas abertas vendo a mesma fila.
 *
 * ## O preço guardado é o de QUANDO entrou — a tela confere o de agora
 *
 * O medo que segurava a fila na aba era real: uma fila de uma semana atrás
 * imprimiria o preço de antes da remarcação. A resposta agora é a tela de
 * Etiquetas reler nome e preço das variantes ao abrir e imprimir o ATUAL,
 * avisando linha a linha o que mudou — e a pré-visualização é a do preço
 * atual, então o papel continua dizendo o que a tela mostrou.
 */

export type LabelQueueItem = {
  readonly variantId: string
  readonly productId: string
  readonly name: string
  readonly sku: string
  readonly gtin: string | null
  /** Decimal na fiação é string (ADR-0016). Nulo é "sem preço cadastrado". */
  readonly salePrice: string | null
  /** Marca e unidade, para os campos do editor (F129). Filas antigas não têm. */
  readonly brand?: string | null
  readonly unit?: string | null
  /** F141: o fornecedor e a localização nesta loja. Filas antigas não têm. */
  readonly supplierReference?: string | null
  readonly location?: string | null
  readonly quantity: number
  /** Quando entrou na fila (ISO). Filas de antes da F136 não têm. */
  readonly addedAt?: string
}

const STORAGE_KEY = 'aeris.label-queue'

/**
 * Cabe numa folha e cabe na cabeça de quem confere.
 *
 * Não é limite técnico — é o ponto em que a tela deixa de ser conferível. Mil
 * etiquetas enfileiradas por engano seriam mil adesivos gastos antes de alguém
 * perceber.
 */
export const LABEL_QUEUE_MAX = 200

type StoredQueue = {
  /** De qual loja é esta fila. */
  readonly tenantId: string
  readonly items: readonly LabelQueueItem[]
}

/**
 * O último texto lido do armazenamento, e o objeto que ele produziu.
 *
 * Guardar só o objeto seria mais simples e estaria errado de duas maneiras: a
 * fila deixaria de ver qualquer escrita que não passasse por aqui, e — o que
 * importa de verdade — não haveria como saber se ela ainda é a mesma. Guardar
 * o TEXTO permite reler barato e devolver a MESMA referência enquanto nada
 * mudou, que é o que `useSyncExternalStore` exige para não entrar em laço.
 */
let cache: { readonly raw: string; readonly queue: StoredQueue } | null = null
const listeners = new Set<() => void>()

const EMPTY: readonly LabelQueueItem[] = []

const isItem = (value: unknown): value is LabelQueueItem => {
  if (typeof value !== 'object' || value === null) return false
  const row = value as Record<string, unknown>
  return (
    typeof row.variantId === 'string' &&
    typeof row.productId === 'string' &&
    typeof row.name === 'string' &&
    typeof row.sku === 'string' &&
    (row.gtin === null || typeof row.gtin === 'string') &&
    (row.salePrice === null || typeof row.salePrice === 'string') &&
    (row.brand == null || typeof row.brand === 'string') &&
    (row.unit == null || typeof row.unit === 'string') &&
    (row.supplierReference == null ||
      typeof row.supplierReference === 'string') &&
    (row.location == null || typeof row.location === 'string') &&
    (row.addedAt === undefined || typeof row.addedAt === 'string') &&
    typeof row.quantity === 'number' &&
    Number.isFinite(row.quantity) &&
    row.quantity > 0
  )
}

/**
 * O texto guardado, trazendo a fila de antes da F136 junto.
 *
 * Quem estava com uma fila aberta na aba quando a versão nova entrou não perde
 * o que já tinha mandado: a fila da sessão passa para o computador uma vez, e
 * sai da sessão para não voltar a ser copiada por cima de uma fila mais nova.
 */
const readRaw = (): string | null => {
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored !== null) return stored
  const fromSession = window.sessionStorage.getItem(STORAGE_KEY)
  if (fromSession === null) return null
  window.localStorage.setItem(STORAGE_KEY, fromSession)
  window.sessionStorage.removeItem(STORAGE_KEY)
  return fromSession
}

const load = (): StoredQueue | null => {
  let raw: string | null
  try {
    raw = readRaw()
  } catch {
    // Aba anônima ou armazenamento bloqueado. O que estiver em memória vale.
    return cache?.queue ?? null
  }
  if (raw === null) return null
  if (cache !== null && cache.raw === raw) return cache.queue

  try {
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const row = parsed as Record<string, unknown>
    if (typeof row.tenantId !== 'string' || !Array.isArray(row.items)) {
      return null
    }
    /*
     * As linhas são conferidas uma a uma.
     *
     * O que está no armazenamento pode ser a fila de uma versão anterior, ou
     * ter sido editada à mão. Uma linha sem SKU imprimiria um adesivo sem
     * código de barras e sem aviso; uma com quantidade `null` imprimiria
     * `NaN` etiquetas. Linha que não convence é descartada, não corrigida.
     */
    const queue: StoredQueue = {
      tenantId: row.tenantId,
      items: row.items.filter(isItem),
    }
    cache = { raw, queue }
    return queue
  } catch {
    // JSON pela metade. Fila vazia imprime nada, que é o pior resultado
    // aceitável; fila corrompida imprimiria qualquer coisa.
    return null
  }
}

const save = (queue: StoredQueue): void => {
  const raw = JSON.stringify(queue)
  cache = { raw, queue }
  try {
    window.localStorage.setItem(STORAGE_KEY, raw)
  } catch {
    // A fila em memória continua valendo: quem está com a tela aberta imprime.
  }
  for (const listener of listeners) listener()
}

/*
 * Outra aba (ou outra janela do app, no PC Mode) mexeu na fila.
 *
 * O `storage` só dispara nas OUTRAS páginas, nunca na que escreveu — a que
 * escreveu já avisa pelo `save`. `key === null` é o `clear()` do armazenamento
 * inteiro. O `load` relê o texto e só troca a referência se ele mudou.
 */
const onStorage = (event: StorageEvent): void => {
  if (event.key !== STORAGE_KEY && event.key !== null) return
  for (const listener of listeners) listener()
}

export const subscribeToLabelQueue = (listener: () => void): (() => void) => {
  if (listeners.size === 0) window.addEventListener('storage', onStorage)
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) window.removeEventListener('storage', onStorage)
  }
}

/**
 * A fila DESTA loja.
 *
 * O `tenantId` não é decoração: trocar de empresa na mesma aba não pode levar
 * nomes e preços de uma loja para a etiqueta da outra. Fila de outra conta é
 * tratada como fila vazia — nunca mesclada, nunca mostrada.
 *
 * Devolve a MESMA referência enquanto nada muda, porque `useSyncExternalStore`
 * compara por identidade e um array novo a cada leitura é um laço infinito de
 * renderização.
 */
export const readLabelQueue = (
  tenantId: string | null
): readonly LabelQueueItem[] => {
  if (tenantId === null) return EMPTY
  const queue = load()
  if (queue?.tenantId !== tenantId) return EMPTY
  return queue.items
}

const currentItems = (tenantId: string): readonly LabelQueueItem[] => {
  const queue = load()
  return queue?.tenantId === tenantId ? queue.items : []
}

/**
 * Acrescenta, **somando a quantidade** quando a variante já está na fila.
 *
 * Mandar o mesmo produto duas vezes quer dizer "quero mais etiquetas dele", e
 * não "quero duas linhas iguais para conferir". Duas linhas iguais é uma tela
 * que a pessoa tem de somar de cabeça antes de confiar.
 *
 * ## O que não coube é DITO (F195)
 *
 * Até a F195 a 201ª variante sumia calada: o bipe limpava o campo, a tela não
 * dizia nada, e a peça ficava sem etiqueta. Agora a conta volta a quem chamou
 * — `dropped` são as variantes NOVAS que não couberam — e quem chamou avisa.
 * Somar numa variante que já está na fila sempre cabe: não cria linha.
 */
export type LabelQueueAddition = {
  /** Variantes que viraram linha nova. */
  readonly added: number
  /** Variantes que já estavam e ganharam mais etiquetas. */
  readonly increased: number
  /** Variantes novas que não couberam no teto de `LABEL_QUEUE_MAX` linhas. */
  readonly dropped: number
}

export const addToLabelQueue = (
  tenantId: string,
  additions: readonly LabelQueueItem[]
): LabelQueueAddition => {
  const items = [...currentItems(tenantId)]
  const now = new Date().toISOString()
  let added = 0
  let increased = 0
  let dropped = 0

  for (const addition of additions) {
    if (addition.quantity <= 0) continue
    const at = items.findIndex((item) => item.variantId === addition.variantId)
    if (at === -1) {
      if (items.length >= LABEL_QUEUE_MAX) {
        dropped += 1
        continue
      }
      items.push({ ...addition, addedAt: addition.addedAt ?? now })
      added += 1
      continue
    }
    const existing = items[at]
    if (existing === undefined) continue
    items[at] = {
      // O produto é o mesmo, mas o nome e o preço vêm da leitura mais recente:
      // se a fila está aberta desde antes de uma correção de cadastro, a linha
      // certa é a nova.
      ...addition,
      quantity: existing.quantity + addition.quantity,
      // "Na fila desde" é de quando entrou pela PRIMEIRA vez.
      addedAt: existing.addedAt ?? addition.addedAt ?? now,
    }
    increased += 1
  }

  save({ tenantId, items })
  return { added, increased, dropped }
}

/** "3 produtos não couberam na fila (…)" — ou nulo quando tudo coube. */
export const droppedMessage = (dropped: number): string | null =>
  dropped === 0
    ? null
    : `${String(dropped)} ${dropped === 1 ? 'produto não coube' : 'produtos não couberam'} na fila — ela guarda até ${String(LABEL_QUEUE_MAX)} produtos. Imprima ou limpe a fila e mande de novo.`

/**
 * A ordem da fila (F195).
 *
 * `arrival` é a de sempre — a ordem em que entraram. As outras são para colar:
 * quem etiqueta a vitrine quer a folha na ordem da prateleira (`location`), e
 * quem confere a nota quer por nome ou por código. A IMPRESSÃO segue a ordem
 * escolhida: a pessoa cola na ordem que vê.
 */
export type LabelQueueOrder = 'arrival' | 'name' | 'sku' | 'location'

export const LABEL_QUEUE_ORDERS: readonly {
  readonly value: LabelQueueOrder
  readonly label: string
}[] = [
  { value: 'arrival', label: 'Chegada' },
  { value: 'name', label: 'Nome' },
  { value: 'sku', label: 'Código' },
  { value: 'location', label: 'Local' },
]

const collator = new Intl.Collator('pt-BR', {
  numeric: true,
  sensitivity: 'base',
})

/**
 * Ordena sem mexer na fila guardada. `numeric`: "A9" antes de "A10", como se
 * lê. Sem local vai para o fim — é a peça que ninguém sabe onde fica, e não a
 * primeira da prateleira. Empate fica na ordem de chegada.
 */
export const sortLabelQueue = (
  items: readonly LabelQueueItem[],
  order: LabelQueueOrder
): readonly LabelQueueItem[] => {
  if (order === 'arrival') return items
  const keyOf = (item: LabelQueueItem): string | null => {
    if (order === 'name') return item.name
    if (order === 'sku') return item.sku
    const location = item.location?.trim() ?? ''
    return location.length === 0 ? null : location
  }
  return items
    .map((item, index) => ({ item, index, key: keyOf(item) }))
    .sort((a, b) => {
      if (a.key === null || b.key === null) {
        if (a.key !== b.key) return a.key === null ? 1 : -1
        return a.index - b.index
      }
      return collator.compare(a.key, b.key) || a.index - b.index
    })
    .map(({ item }) => item)
}

export const setLabelQuantity = (
  tenantId: string,
  variantId: string,
  quantity: number
): void => {
  const items = currentItems(tenantId)
    .map((item) =>
      item.variantId === variantId
        ? { ...item, quantity: Math.max(0, Math.floor(quantity)) }
        : item
    )
    // Zero não é uma linha: quem zera está removendo, e deixar a linha lá com
    // "0" faz a contagem de etiquetas discordar da contagem de linhas.
    .filter((item) => item.quantity > 0)
  save({ tenantId, items })
}

export const removeFromLabelQueue = (
  tenantId: string,
  variantId: string
): void => {
  save({
    tenantId,
    items: currentItems(tenantId).filter(
      (item) => item.variantId !== variantId
    ),
  })
}

export const clearLabelQueue = (tenantId: string): void => {
  save({ tenantId, items: [] })
}

/** Quantos adesivos sairão — não quantas linhas a tela tem. */
export const labelCount = (items: readonly LabelQueueItem[]): number =>
  items.reduce((total, item) => total + item.quantity, 0)
